import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, FileText, TrendingUp, Users, Clock, ChevronRight, Search } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { formatDateTime } from '../../utils/timeUtils';

interface PaperReviewItem {
  paperId: string;
  paperTitle: string;
  packageInfo: {
    organization: string;
    category: string;
    level_text: string;
  };
  overallAccuracy: number;
  studentCount: number;
  lastAnswerTime: string | null;
}

export default function PaperReviewList() {
  const navigate = useNavigate();
  const [papers, setPapers] = useState<PaperReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadPapers();
  }, []);

  const loadPapers = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const session = await supabase.auth.getSession();
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.data.session?.access_token}`,
        },
        body: JSON.stringify({ action: 'get_papers' }),
      });

      const result = await response.json();
      if (result.success) {
        setPapers(result.data || []);
      }
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const filteredPapers = papers.filter(paper =>
    paper.paperTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
    paper.packageInfo.organization.toLowerCase().includes(searchQuery.toLowerCase()) ||
    paper.packageInfo.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    paper.packageInfo.level_text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 80) return 'text-green-600 bg-green-50';
    if (accuracy >= 60) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-purple-600" />
          评讲试卷
        </h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="搜索试卷名称、考评体系、课程分类或级别..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">试卷名称</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">所属刷题包</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">整体正确率</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">已刷题学员数</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">最近答题时间</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPapers.length > 0 ? (
                filteredPapers.map((paper, index) => (
                  <motion.tr
                    key={paper.paperId}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-medium text-gray-800">{paper.paperTitle}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">{paper.packageInfo.organization}</span>
                        <span className="text-gray-400 mx-1">/</span>
                        <span>{paper.packageInfo.category}</span>
                        <span className="text-gray-400 mx-1">/</span>
                        <span>{paper.packageInfo.level_text}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getAccuracyColor(paper.overallAccuracy)}`}>
                        <TrendingUp className="w-3 h-3 mr-1" />
                        {paper.overallAccuracy}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span>{paper.studentCount}人</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span>{paper.lastAnswerTime ? formatDateTime(paper.lastAnswerTime) : '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/teacher/paper-review/${paper.paperId}`)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      >
                        评讲
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <div className="text-gray-400">
                      <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>暂无试卷数据</p>
                      <p className="text-sm text-gray-400 mt-1">请先在刷题套餐中购买套餐</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
